import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useLocalStorage } from '@buffer-mono/popcorn'

import type { CommentsChannelView } from '~publish/legacy/routes'

import { isEngagementDetailsPath } from '../util/routeUtils'

export const COMMENTS_VIEW_PREFERENCE_STORAGE_KEY = 'comments-view-preference'
const DEFAULT_VIEW: CommentsChannelView = 'posts'

/**
 * Checks if the URL path explicitly indicates a view.
 * "posts" (plural) or "by-post" = explicit posts view.
 * A bare /community or /channels/:id/community is ambiguous.
 */
function hasExplicitViewInPath(pathname: string): boolean {
  const pathParts = pathname.split('/')
  return pathParts.includes('posts') || pathParts.includes('by-post')
}

/**
 * Derives the current view from the URL path.
 */
function deriveViewFromPath(pathname: string): CommentsChannelView {
  return hasExplicitViewInPath(pathname) ? 'posts' : 'comments'
}

/**
 * Appends /posts to the current pathname for redirect.
 */
function buildPostsViewPath(pathname: string, search: string): string {
  const base = pathname.replace(/\/$/, '')
  return `${base}/posts${search}`
}

/**
 * The view an ambiguous Community URL resolves to for this reader: the
 * persisted preference, or the default. Read-only, with none of the redirect
 * or persist side effects of `usePersistedViewPreference`.
 */
export function usePersistedView(): CommentsChannelView {
  const [persistedView] = useLocalStorage<CommentsChannelView | undefined>(
    COMMENTS_VIEW_PREFERENCE_STORAGE_KEY,
    undefined,
  )
  return persistedView ?? DEFAULT_VIEW
}

/**
 * Manages the community view preference with cross-session persistence.
 *
 * The selected view is stored in localStorage so it survives navigation
 * and new sessions. On mount at an ambiguous URL (no explicit view in path),
 * redirects to the persisted preference or the default ('posts').
 *
 * Priority: explicit URL > localStorage > default ('posts').
 *
 * `isGoogleBusiness` tri-state:
 *   - `undefined` → unknown (e.g. channel still loading); skip redirect and
 *     skip auto-persist until resolved, to avoid a race that redirects a GBP
 *     URL to `/posts` or clobbers the cross-channel preference from a GBP
 *     deep link.
 *   - `true` → force 'comments' view; skip redirect and auto-persist.
 *   - `false` → normal behavior.
 */
export function usePersistedViewPreference({
  isGoogleBusiness,
}: { isGoogleBusiness?: boolean } = {}): {
  view: CommentsChannelView
  setViewPreference: (view: CommentsChannelView) => void
} {
  const location = useLocation()
  const navigate = useNavigate()
  const initializedRef = useRef(false)

  const [persistedView, setPersistedView] = useLocalStorage<
    CommentsChannelView | undefined
  >(COMMENTS_VIEW_PREFERENCE_STORAGE_KEY, undefined)

  const urlHasExplicitView = hasExplicitViewInPath(location.pathname)
  const currentViewFromUrl = deriveViewFromPath(location.pathname)
  const effectivePreference = persistedView ?? DEFAULT_VIEW

  // The view (posts/list) is a sub-state of the Comments tab — it has no
  // meaning on the Mentions tab, which doesn't render the
  // posts/list toggle. When the URL carries a non-Comments tab, the
  // preference-based view resolution and the redirect both stand down so
  // the URL stays at the consumer's chosen tab (e.g. /community?tab=mentions)
  // instead of getting forced to /community/posts?tab=mentions on every
  // navigation in.
  const searchParams = new URLSearchParams(location.search)
  const tabParam = searchParams.get('tab')
  const isOnCommentsTab = !tabParam || tabParam === 'comments'

  // Resolve the view to render. Order of preference:
  //   1. GBP channel — always 'comments' (the toggle isn't shown anyway).
  //   2. Explicit URL — trust it (e.g. a deep link to /community/posts).
  //   3. Ambiguous URL on the Comments tab, non-GBP — use the persisted
  //      preference immediately. The redirect effect below will sync the
  //      URL, but resolving the preference here means the toggle and
  //      rendered content start in the right state from the first paint,
  //      avoiding a comments → posts flash when navigating in from
  //      elsewhere in the app.
  //   4. Ambiguous URL on a non-Comments tab — return the URL-derived
  //      'comments' value. The toggle isn't rendered on these tabs, so the
  //      value is effectively unused; the important thing is that we
  //      DON'T return 'posts' here, which would cascade into a redirect
  //      that drags the URL toward /posts on every mentions-tab navigation.
  //   5. Ambiguous URL, GBP status still loading — fall back to the URL
  //      so we don't briefly show 'posts' to a user whose final view is
  //      'comments'.
  const view = useMemo<CommentsChannelView>(() => {
    if (isGoogleBusiness === true) return 'comments'
    if (urlHasExplicitView) return currentViewFromUrl
    if (isGoogleBusiness === false && isOnCommentsTab)
      return effectivePreference
    return currentViewFromUrl
  }, [
    isGoogleBusiness,
    urlHasExplicitView,
    currentViewFromUrl,
    effectivePreference,
    isOnCommentsTab,
  ])

  // On mount: redirect to posts view when on ambiguous URL and preference
  // is 'posts' (or no preference yet, since default is 'posts').
  // Only redirect once we've confirmed the channel is not GBP (=== false);
  // `undefined` means still loading and we must wait. The Comments-tab
  // guard prevents the redirect from hijacking a navigation to a different
  // tab (Mentions) — that tab doesn't care about /posts and forcing
  // the URL there would override the user's tab click.
  // The `initializedRef` guard lives inside the effect (not in the computed
  // condition) because refs aren't reactive — including them in deps lists is
  // misleading, and reading them outside the effect risks stale reads.
  // Engagement deep links (`/community/comment/:id`, `/community/mention/:id`)
  // opt out: appending /posts would rewrite them to a route that doesn't exist.
  const shouldRedirect =
    !urlHasExplicitView &&
    !isEngagementDetailsPath(location.pathname) &&
    isGoogleBusiness === false &&
    effectivePreference === 'posts' &&
    isOnCommentsTab

  useEffect(() => {
    if (!shouldRedirect || initializedRef.current) return
    initializedRef.current = true
    navigate(buildPostsViewPath(location.pathname, location.search), {
      replace: true,
    })
  }, [shouldRedirect, navigate, location.pathname, location.search])

  // Auto-persist when arriving at an explicit URL (e.g. deep link, bookmark).
  // Skip for GBP (URL says 'posts' but view is forced to 'comments' — persisting
  // would clobber the user's cross-channel preference) and while loading.
  useEffect(() => {
    if (urlHasExplicitView && isGoogleBusiness === false) {
      setPersistedView(currentViewFromUrl)
    }
  }, [
    urlHasExplicitView,
    currentViewFromUrl,
    setPersistedView,
    isGoogleBusiness,
  ])

  const setViewPreference = useCallback(
    (newView: CommentsChannelView): void => {
      setPersistedView(newView)
    },
    [setPersistedView],
  )

  return { view, setViewPreference }
}
